import { useState, useMemo, useCallback } from "react";
import {
  useAssignPeriodsMutation,
  useUnassignPeriodsMutation,
} from "store/api/generatedApi";
import type { ReviewPeriodData } from "store/api/generatedApi";

interface UseAssignmentLogicArgs {
  tenant: string;
  periods: ReviewPeriodData[];
  refetchPeriods: () => void;
}

export const useAssignmentLogic = ({
  tenant,
  periods,
  refetchPeriods,
}: UseAssignmentLogicArgs) => {
  const [selectedPeriodIds, setSelectedPeriodIds] = useState(
    () => new Set<number>()
  );
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);

  const [assignPeriods, { isLoading: isAssigning }] =
    useAssignPeriodsMutation();
  const [unassignPeriods, { isLoading: isUnassigning }] =
    useUnassignPeriodsMutation();

  const sortedPeriods = useMemo(
    () => [...periods].sort((a, b) => a.start.localeCompare(b.start)),
    [periods]
  );

  const assignmentStats = useMemo(() => {
    const stats = new Map<string, number>();
    sortedPeriods.forEach((period) => {
      if (period.assigned_to_id) {
        stats.set(
          period.assigned_to_id,
          (stats.get(period.assigned_to_id) ?? 0) + 1
        );
      }
    });
    return stats;
  }, [sortedPeriods]);

  const handleTogglePeriod = useCallback(
    (periodId: number, event?: React.MouseEvent) => {
      if (event?.shiftKey && lastSelectedId !== null) {
        const periodIds = sortedPeriods.map((p) => p.id);
        const lastIndex = periodIds.indexOf(lastSelectedId);
        const currentIndex = periodIds.indexOf(periodId);

        if (lastIndex !== -1 && currentIndex !== -1) {
          const start = Math.min(lastIndex, currentIndex);
          const end = Math.max(lastIndex, currentIndex);
          const rangeIds = periodIds.slice(start, end + 1);

          setSelectedPeriodIds((prev) => new Set([...prev, ...rangeIds]));
        }
      } else {
        setSelectedPeriodIds((prev) => {
          const next = new Set(prev);
          if (next.has(periodId)) {
            next.delete(periodId);
          } else {
            next.add(periodId);
          }
          return next;
        });
      }
      setLastSelectedId(periodId);
    },
    [lastSelectedId, sortedPeriods]
  );

  const handleManualAssign = useCallback(
    async (userId: string) => {
      if (!userId || selectedPeriodIds.size === 0) return;

      await assignPeriods({
        tenantName: tenant,
        periodIds: Array.from(selectedPeriodIds),
        assignedToId: userId,
      }).unwrap();

      setSelectedPeriodIds(new Set());
      refetchPeriods();
    },
    [selectedPeriodIds, assignPeriods, tenant, refetchPeriods]
  );

  const handleUnassign = useCallback(async () => {
    if (selectedPeriodIds.size === 0) return;

    await unassignPeriods({
      tenantName: tenant,
      periodIds: Array.from(selectedPeriodIds),
    }).unwrap();

    setSelectedPeriodIds(new Set());
    refetchPeriods();
  }, [selectedPeriodIds, unassignPeriods, tenant, refetchPeriods]);

  const handleUnassignAll = useCallback(async () => {
    const assignedPeriods = sortedPeriods.filter((p) => p.assigned_to_id);
    if (assignedPeriods.length === 0) return;

    await unassignPeriods({
      tenantName: tenant,
      periodIds: assignedPeriods.map((p) => p.id),
    }).unwrap();

    refetchPeriods();
  }, [sortedPeriods, unassignPeriods, tenant, refetchPeriods]);

  const handleAutoDistribute = useCallback(
    async (userIds: string[]) => {
      if (userIds.length === 0 || sortedPeriods.length === 0) return;

      const unassignedPeriods = sortedPeriods.filter(
        (p) => !p.assigned_to_id || selectedPeriodIds.has(p.id)
      );
      const periodsPerUser = Math.ceil(
        unassignedPeriods.length / userIds.length
      );

      for (let i = 0; i < userIds.length; i++) {
        const userPeriods = unassignedPeriods.slice(
          i * periodsPerUser,
          (i + 1) * periodsPerUser
        );
        if (userPeriods.length > 0) {
          await assignPeriods({
            tenantName: tenant,
            periodIds: userPeriods.map((p) => p.id),
            assignedToId: userIds[i] ?? "",
          }).unwrap();
        }
      }

      setSelectedPeriodIds(new Set());
    },
    [sortedPeriods, selectedPeriodIds, assignPeriods, tenant]
  );

  return {
    selectedPeriodIds,
    sortedPeriods,
    assignmentStats,
    isAssigning,
    isUnassigning,
    handleTogglePeriod,
    handleManualAssign,
    handleUnassign,
    handleUnassignAll,
    handleAutoDistribute,
  };
};
